import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import Chart from "react-apexcharts";
import React, { useMemo, useCallback } from "react";
import { ApexOptions } from "apexcharts";
import { InteractionsByYear } from "../../types/dashboard/Dashboard";
import LoadingIndicator from "../../components/common/LoadingIndicator/LoadingIndicator";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDashboardDrill } from "../../hooks/useDashboardDrill";
import { numberWithCommas } from "../../helpers/numberWithCommas";

interface InteractionAnalyticsBarChartProps {
  interactionsByYear: InteractionsByYear | undefined;
  isBarChartLoading: boolean;
}

const yAxisFormatter = (value: number) => numberWithCommas(Math.floor(value))!;

function InteractionAnalyticsBarChart({
  interactionsByYear,
  isBarChartLoading,
}: Readonly<InteractionAnalyticsBarChartProps>) {
  const { t } = useTranslation(["dashboards", "common"]);
  const theme = useTheme();
  const { drill } = useDashboardDrill("v1", false);
  const primaryColor = theme.palette.customColors?.wDarkBlue;

  // Derive chart data synchronously — avoids the empty-first-render caused by useState+useEffect
  const chartData = useMemo(() => {
    if (!interactionsByYear) return null;

    const days = Object.keys(interactionsByYear);
    const count = Object.values(interactionsByYear);
    const dates = days.map((day) => dayjs.utc(day).format("YYYY"));

    const maxValue = Math.max(...count);
    const minVisibleValue = maxValue * 0.01;
    const adjustedSeries = count.map((value) =>
      value > 0 && value < minVisibleValue ? minVisibleValue : value
    );

    return { categories: dates, series: adjustedSeries, actualValues: count };
  }, [interactionsByYear]);

  const handleBarClick = useCallback(
    (_event: any, _chartContext: any, config: any) => {
      if (!chartData) return;
      const yearStr = chartData.categories[config.dataPointIndex];
      const start = dayjs(yearStr, "YYYY").utc();
      const end = start.add(1, "year");
      drill(
        {
          kind: "dateRange",
          from: start.format("YYYY-MM-DDTHH:mm:ss"),
          to: end.format("YYYY-MM-DDTHH:mm:ss"),
        },
        {},
      );
    },
    [chartData, drill],
  );

  const handleMouseMove = useCallback(
    (_event: any, chartContext: any, config: any) => {
      const chartElement = chartContext.el;
      chartElement.style.cursor =
        config.dataPointIndex !== -1 ? "pointer" : "default";
    },
    []
  );

  // Memoize options+series so ApexCharts doesn't re-initialize on parent re-renders
  const chartConfig = useMemo(() => {
    if (!chartData) return null;

    const options: ApexOptions = {
      chart: {
        type: "bar",
        toolbar: { show: true, offsetY: -30 },
        animations: { enabled: false },
        events: {
          dataPointSelection: handleBarClick,
          mouseMove: handleMouseMove,
        },
      },
      dataLabels: { enabled: false },
      xaxis: { categories: chartData.categories },
      yaxis: {
        labels: { formatter: yAxisFormatter },
        forceNiceScale: true,
      },
      plotOptions: { bar: { borderRadius: 4, horizontal: false } },
      colors: [primaryColor],
      fill: { opacity: 1 },
      tooltip: {
        y: {
          formatter: (_value, { dataPointIndex }) => {
            const actualValue = chartData.actualValues[dataPointIndex];
            return numberWithCommas(actualValue) || "0";
          },
        },
      },
    };

    const series = [
      { name: t("common:interactions"), data: chartData.series },
    ];

    return { options, series };
  }, [chartData, handleBarClick, handleMouseMove, primaryColor, t]);

  if (isBarChartLoading) {
    return (
      <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LoadingIndicator />
        </CardContent>
      </Card>
    );
  }

  if (!chartConfig) {
    return (
      <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <CardContent>
          <Typography variant="h6" sx={{ flexShrink: 0 }}>
            {t("dashboards:interactionsByYear")}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            No data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          "&:last-child": { pb: 1 },
        }}
      >
        <Typography variant="h6" sx={{ flexShrink: 0 }}>
          {t("dashboards:interactionsByYear")}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Chart
            options={chartConfig.options}
            series={chartConfig.series}
            type="bar"
            height="100%"
          />
        </Box>
      </CardContent>
    </Card>
  );
}

export default React.memo(InteractionAnalyticsBarChart);
